import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PartyPopper, Trash2, Star, Send } from "lucide-react";

const CATEGORIES = ["Non-scale victory", "Strength PR", "Nutrition", "Mindset", "Energy", "Milestone"];

export function WinsWall({ isCoach = false }) {
  const [wins, setWins] = useState([]);
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/wins").then((r) => setWins(r.data)), []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const post = async (e) => {
    e.preventDefault();
    if (content.trim().length < 3) return;
    setBusy(true);
    try {
      await api.post("/wins", { content, category: category || null });
      toast.success("Win posted — celebrate it!");
      setContent(""); setCategory("");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cheer = async (w) => {
    const { data } = await api.post(`/wins/${w.win_id}/cheer`);
    setWins((prev) => prev.map((x) => (x.win_id === w.win_id ? { ...x, cheer_count: data.cheer_count, cheered_by_me: data.cheered } : x)));
  };

  const feature = async (w) => {
    await api.patch(`/wins/${w.win_id}/feature`);
    toast.success(w.featured ? "Un-featured" : "Featured at the top");
    load();
  };

  const remove = async (w) => {
    if (!window.confirm("Remove this win?")) return;
    await api.delete(`/wins/${w.win_id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div className="space-y-6" data-testid="wins-wall">
      {!isCoach && (
        <form onSubmit={post} className="card-luxe space-y-4" data-testid="win-composer">
          <div className="overline">Share a win</div>
          <textarea className="input-luxe" rows={3} maxLength={600} value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Big or small — hit a PR, said no to the vending machine, slept 8 hours…"
            data-testid="win-content" />
          <div className="flex gap-2 flex-wrap">
            {CATEGORIES.map((c) => (
              <button key={c} type="button" onClick={() => setCategory(category === c ? "" : c)}
                className={category === c ? "btn-primary text-xs" : "btn-secondary text-xs"}
                data-testid={`win-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c}</button>
            ))}
          </div>
          <button className="btn-primary" disabled={busy || content.trim().length < 3} data-testid="win-submit">
            <Send size={13} /> Post to the wall
          </button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {wins.map((w) => (
          <article key={w.win_id} className="card-luxe" data-testid={`win-${w.win_id}`}
            style={w.featured ? { borderColor: "var(--accent)" } : undefined}>
            <div className="flex items-center justify-between">
              <div className="overline flex items-center gap-2">
                {w.featured && <Star size={11} style={{ color: "var(--accent)" }} data-testid={`win-featured-${w.win_id}`} />}
                {w.author_name}{w.category ? ` · ${w.category}` : ""}
              </div>
              <div className="flex items-center gap-1">
                {isCoach && (
                  <button className="btn-ghost" onClick={() => feature(w)} title="Feature" data-testid={`feature-win-${w.win_id}`}>
                    <Star size={13} style={{ color: w.featured ? "var(--accent)" : undefined }} />
                  </button>
                )}
                {(isCoach || w.mine) && (
                  <button className="btn-ghost" onClick={() => remove(w)} data-testid={`delete-win-${w.win_id}`}><Trash2 size={13} /></button>
                )}
              </div>
            </div>
            <p className="font-soft text-base mt-3 leading-relaxed whitespace-pre-wrap" style={{ color: "var(--text-primary)" }}>{w.content}</p>
            <div className="mt-4 flex items-center justify-between">
              <button onClick={() => cheer(w)} data-testid={`cheer-win-${w.win_id}`}
                className="flex items-center gap-2 font-label text-[10px] tracking-[0.2em] uppercase px-3 py-2 transition-colors"
                style={{
                  border: "1px solid var(--border-strong)",
                  background: w.cheered_by_me ? "var(--accent)" : "transparent",
                  color: w.cheered_by_me ? "#fff" : "var(--text-secondary)",
                }}>
                <PartyPopper size={12} /> {w.cheer_count || 0} {w.cheer_count === 1 ? "cheer" : "cheers"}
              </button>
              <span className="text-xs text-tertiary-luxe">{(w.created_at || "").slice(0, 10)}</span>
            </div>
          </article>
        ))}
        {wins.length === 0 && (
          <div className="text-secondary-luxe text-sm" data-testid="wins-empty">
            No wins posted yet — be the first on the wall.
          </div>
        )}
      </div>
    </div>
  );
}
